<template>
  <WidgetCadre
    titre="Mon équipe"
    v-bind="apparence"
    lien="/projets/mes-taches"
    libelle-lien="Mes tâches"
    :chargement="chargement"
    :panne="panne"
    :reglable="reglable"
    @retirer="$emit('retirer')"
    @recharger="$emit('recharger')"
  >
    <div class="equipes">
      <section
        v-for="equipe in equipes"
        :key="equipe.id"
        class="equipe"
      >
        <header class="equipe__tete">
          <span
            class="equipe__pastille"
            :style="{ backgroundColor: equipe.couleur || 'var(--el-color-primary)' }"
          />
          <h4 class="equipe__nom">
            {{ equipe.nom }}
          </h4>
          <button
            v-if="equipe.a_prendre"
            class="equipe__prendre"
            type="button"
            @click="router.push({ path: '/projets/mes-taches', query: { a_prendre: 1 } })"
          >
            {{ equipe.a_prendre }} à prendre
          </button>
        </header>

        <ul
          v-if="equipe.bloquees?.length"
          class="bloquees"
        >
          <li
            v-for="etape in equipe.bloquees"
            :key="etape.etape_id"
          >
            <button
              class="bloquee"
              type="button"
              @click="router.push({ path: `/processus/dossiers/${etape.dossier_id}`, query: { etape: etape.etape_id } })"
            >
              <Icon
                class="bloquee__marque"
                icon="mdi:alert-circle-outline"
                :width="13"
              />
              <span class="bloquee__libelle">{{ etape.libelle }}</span>
              <span class="bloquee__dossier">{{ etape.dossier }}</span>
              <span class="bloquee__depuis">{{ depuis(etape) }}</span>
            </button>
          </li>
          <li
            v-if="autres(equipe)"
            class="bloquees__autres"
          >
            + {{ autres(equipe) }} autre{{ autres(equipe) > 1 ? 's' : '' }}
          </li>
        </ul>

        <ul
          v-if="equipe.charge?.length"
          class="charge"
        >
          <li
            v-for="membre in equipe.charge"
            :key="membre.user_id"
            class="charge__ligne"
          >
            <span class="charge__nom">{{ membre.nom }}</span>
            <span
              class="charge__mesure"
              title="Cartes"
            >{{ membre.cartes }} carte{{ membre.cartes > 1 ? 's' : '' }}</span>
            <span
              v-if="membre.cartes_en_retard"
              class="charge__mesure charge__mesure--retard"
              title="Cartes en retard"
            >dont {{ membre.cartes_en_retard }} en retard</span>
            <span
              class="charge__mesure"
              title="Étapes de processus"
            >{{ membre.etapes }} étape{{ membre.etapes > 1 ? 's' : '' }}</span>
            <span
              v-if="membre.etapes_en_cours"
              class="charge__mesure"
              title="Étapes en cours"
            >dont {{ membre.etapes_en_cours }} en cours</span>
          </li>
        </ul>
      </section>

      <p
        v-if="!equipes.length"
        class="equipes__vide"
      >
        Aucune équipe à suivre pour l'instant.
      </p>
    </div>
  </WidgetCadre>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { Icon } from '@iconify/vue'
import WidgetCadre from './WidgetCadre.vue'
import { apparenceDe } from './apparence'

/**
 * Par équipe : ce qui attend d'être pris, ce qui est bloqué, et qui porte quoi.
 * Les compteurs viennent du serveur ; le widget n'en recalcule aucun, sauf le
 * nombre de jours depuis lequel une étape est bloquée.
 */

const props = defineProps({
    donnees: { type: Object, default: null },
    chargement: { type: Boolean, default: false },
    panne: { type: Boolean, default: false },
    reglable: { type: Boolean, default: false },
})

defineEmits(['retirer', 'recharger'])

const apparence = apparenceDe('mon-equipe')

const router = useRouter()

const equipes = computed(() => props.donnees?.equipes ?? [])

function autres(equipe) {
    return Math.max(0, (equipe.bloquees_total ?? 0) - (equipe.bloquees?.length ?? 0))
}

function depuis(etape) {
    const par = etape.par ? `, par ${etape.par}` : ''

    if (!etape.depuis) return par.slice(2)

    const jours = Math.max(0, Math.floor((Date.now() - new Date(etape.depuis).getTime()) / 86400000))

    return `depuis ${jours} jour${jours > 1 ? 's' : ''}${par}`
}
</script>

<style lang="scss" scoped>
.equipes {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__vide {
    margin: 4px 0;
    font-size: 12.5px;
    color: var(--el-text-color-secondary);
  }
}

.equipe {
  &__tete {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  &__pastille {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__nom {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 13px;
    font-weight: 650;
    color: var(--el-text-color-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__prendre {
    border: none;
    cursor: pointer;
    font-size: 11.5px;
    font-weight: 600;
    border-radius: 6px;
    padding: 2px 8px;
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);

    &:focus-visible { outline: 2px solid var(--el-color-primary); }
  }
}

.bloquees {
  list-style: none;
  margin: 0 0 6px;
  padding: 0;

  &__autres {
    padding: 4px 10px;
    font-size: 11.5px;
    color: var(--el-text-color-secondary);
  }
}

.bloquee {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  box-shadow: inset 3px 0 0 var(--el-color-danger);

  &:hover { background: var(--el-fill-color-light); }

  &:focus-visible { outline: 2px solid var(--el-color-primary); outline-offset: 1px; }

  &__marque {
    color: var(--el-color-danger);
    flex-shrink: 0;
  }

  &__libelle {
    flex: 1;
    min-width: 0;
    font-size: 12.5px;
    color: var(--el-text-color-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__dossier,
  &__depuis {
    font-size: 11.5px;
    color: var(--el-text-color-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__dossier { max-width: 110px; }
}

.charge {
  list-style: none;
  margin: 0;
  padding: 0;

  &__ligne {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 10px;
    padding: 4px 10px;
    font-size: 12px;

    & + & { border-top: 1px solid var(--el-border-color-lighter); }
  }

  &__nom {
    flex: 1 1 110px;
    min-width: 0;
    color: var(--el-text-color-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__mesure {
    color: var(--el-text-color-secondary);
    font-variant-numeric: tabular-nums;

    &--retard {
      color: var(--el-color-danger);
      font-weight: 600;
    }
  }
}

@media (max-width: 600px) {
  .bloquee__dossier { display: none; }
}
</style>
